import { omit } from 'lodash-es'
import { useMemo } from 'react'

import type { Currency, ExchangeRateMap, LoadExchangeRates } from '@fv/models'
import type { DTO } from '@fv/models/core'

import type { LocationLike } from './quote-taxes.js'

const getCurrencies = (locations: LocationLike[] | undefined): Currency[] => {
  const allowed: Currency[] = ['usd']
  const hasCountry = (code: string) =>
    locations?.some(l => l.country?.toLowerCase() === code) ?? false
  if (hasCountry('ca')) allowed.push('cad')
  if (hasCountry('mx')) allowed.push('mxn')
  return allowed
}

export const useCurrencyOptions = (
  locations: LocationLike[] | undefined,
): { text: string; value: Currency }[] => {
  return useMemo(() => {
    const allowedCurrencies = getCurrencies(locations)
    return allowedCurrencies.map(c => ({
      text: c.toUpperCase(),
      value: c,
    }))
  }, [locations])
}

export function extractLoadExchangeRates(
  loadRates?: DTO<LoadExchangeRates>,
): ExchangeRateMap {
  return omit(loadRates || {}, 'date')
}

export type LoadExchangeRateInput = {
  exchangeRates?: DTO<LoadExchangeRates>
  pickupDate?: Date | string
}

type UseExchangeRatesQuery = (
  enabled: boolean,
  date?: Date | string,
) => { data?: ExchangeRateMap }

export function useExchangeRatesForLoad(
  load: LoadExchangeRateInput | undefined,
  enabled: boolean,
  useExchangeRates: UseExchangeRatesQuery,
): ExchangeRateMap {
  const loadRates = extractLoadExchangeRates(load?.exchangeRates)
  const hasLoadRates = Object.keys(loadRates).length > 0
  const query = useExchangeRates(enabled && !hasLoadRates, load?.pickupDate)
  return hasLoadRates ? loadRates : (query.data ?? {})
}
